import Button from "@/components/Common/Button";
import Modal from "@/components/Common/Modal";
import usePackageLimits from "@/hooks/usePackageLimits";
import usePackages, {getPackageIcon} from "@/hooks/usePackages";
import {isMoreInviteModalOpenAtom} from "@/store/jotaiAtoms";
import useUserStore from "@/store/user";
import {useSetAtom} from "jotai";
import times from "lodash/times";
import {FC, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";

const AddMoreInvitesModal: FC = () => {
  const setModalAtom = useSetAtom(isMoreInviteModalOpenAtom);
  const {currency} = usePackageLimits();
  const extendedCurrentPlan = useUserStore(state => state.user.package_subscription);

  const {t} = useTranslation("payments");
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const {packagesList: plans} = usePackages(currency);

  const currentPlan = useMemo(
    () => plans.find(plan => plan.type === extendedCurrentPlan?.name),
    [plans, extendedCurrentPlan],
  );

  return (
    <Modal
      onClose={() => setModalAtom(false)}
      modalTitle="Add more invitations"
      onConfirm={() => setModalAtom(false)}
      isConfirmDisabled={selectedOption === null}>
      <div className="flex w-full flex-col gap-2">
        <span className="text-neutral-grey-dark text-sm font-normal">Choose your bundle</span>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {times(4, index => {
            const shouldShowDisclaimer = index === 3;
            return (
              <button
                onClick={() => {
                  if (selectedOption === index) setSelectedOption(null);
                  else setSelectedOption(index);
                }}
                key={index}
                className={
                  "group border-neutral-white-dark hover:border-primary-medium-solid relative flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border bg-white p-5 transition-all duration-150 hover:shadow-md " +
                  (index === selectedOption ? "border-primary-dark-solid" : "")
                }>
                {shouldShowDisclaimer && (
                  <div className="border-neutral-white-dark absolute -top-4 right-0 left-0 mx-auto w-min rounded-full border bg-white px-3 py-1 shadow-xs">
                    <span className="bg-linear-(--primary-dark-gradient) bg-clip-text text-sm whitespace-nowrap text-transparent">
                      {t("EXTRA_BUYOUTS_most_popular")}
                    </span>
                  </div>
                )}
                <div className="flex w-full flex-col items-start gap-1">
                  <span
                    className={
                      "group-hover:primary-medium-solid text-neutral-black group-hover:text-primary-medium-solid font-semibold transition-all duration-150 " +
                      (selectedOption === index ? "text-primary-dark-solid" : "")
                    }>
                    {(index + 1) * 10} buyouts
                  </span>
                  <span className="text-neutral-grey-dark text-sm">CHF X</span>
                </div>
                <div
                  className={
                    "border-neutral-grey-light group-hover:border-primary-medium-solid h-5 w-5 shrink-0 rounded-full border transition-all duration-150 " +
                    (selectedOption === index ? "border-primary-dark-solid drop-shadow-xs" : "")
                  }>
                  <div
                    className={
                      "h-full w-full rounded-full border border-white transition-all duration-150 " +
                      (selectedOption === index
                        ? "bg-primary-dark-solid group-hover:bg-primary-medium-solid"
                        : "bg-white")
                    }></div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex w-full flex-col gap-2">
        <span className="text-neutral-grey-dark text-sm font-normal">Your current package</span>
        <div className="border-neutral-grey-light bg-neutral-white-dark grid grid-cols-1 gap-5 rounded-lg border p-2.5 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              {getPackageIcon(
                currentPlan?.type!,
                "h-[30px] w-[30px] overflow-visible rounded-full border border-neutral-grey-light p-1.5",
              )}
              <span>{currentPlan?.name}</span>
            </div>
            <span className="text-neutral-black pl-1 text-xs">
              {extendedCurrentPlan?.invitations_per_campaign ?? 0} invites total
            </span>
          </div>
          <Button buttonStyle="Blue" onClick={() => setModalAtom(false)}>
            Contact sales team to change
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddMoreInvitesModal;
